/**
 * What a thread change is worth interrupting the user for.
 *
 * The rule is deliberately narrow. A notification earns its place only when
 * the user cannot already see the answer: the thread is not the one on screen,
 * the change is an ending rather than progress, and it has not been announced
 * before. Everything else — a turn starting, a thread the user is watching, a
 * state that has not moved since the last look — stays silent, because a
 * notification that fires for routine activity teaches people to ignore it.
 */

export type ThreadNotificationKind = "completed" | "failed" | "needs-input" | "needs-approval";

export interface ThreadNotificationState {
  readonly threadKey: string;
  readonly title: string;
  /** The instant that identifies this ending; a repeat of it is the same event. */
  readonly at: string | null;
  readonly kind: ThreadNotificationKind | null;
}

export interface ThreadNotification {
  readonly threadKey: string;
  readonly title: string;
  readonly kind: ThreadNotificationKind;
  /** Stored so the same ending is never announced twice. */
  readonly at: string;
}

export function threadNotificationKind(thread: {
  readonly hasPendingApprovals?: boolean | undefined;
  readonly hasPendingUserInput?: boolean | undefined;
  readonly session?: { readonly status?: string | undefined } | null | undefined;
  readonly latestTurn?: { readonly state?: string | undefined } | null | undefined;
}): ThreadNotificationKind | null {
  if (thread.hasPendingApprovals === true) return "needs-approval";
  if (thread.hasPendingUserInput === true) return "needs-input";
  if (thread.session?.status === "error") return "failed";
  if (thread.latestTurn?.state === "completed") return "completed";
  return null;
}

export const THREAD_NOTIFICATION_MESSAGES: Record<ThreadNotificationKind, string> = {
  completed: "finished",
  failed: "failed",
  "needs-input": "needs your input",
  "needs-approval": "needs your approval",
};

/**
 * The notifications to raise for this snapshot, given what was already
 * announced. `announced` maps a thread key to the instant last announced for
 * it, so a re-render, a reconnect, or a projection replay that hands back the
 * same state raises nothing.
 */
export function planThreadNotifications(input: {
  readonly threads: readonly ThreadNotificationState[];
  readonly announced: ReadonlyMap<string, string>;
  /** The thread the user is looking at; its endings need no announcement. */
  readonly activeThreadKey: string | null;
  readonly enabled: boolean;
}): readonly ThreadNotification[] {
  if (!input.enabled) return [];
  return input.threads.flatMap((thread) => {
    if (thread.kind === null || thread.at === null) return [];
    if (thread.threadKey === input.activeThreadKey) return [];
    if (input.announced.get(thread.threadKey) === thread.at) return [];
    return [{ threadKey: thread.threadKey, title: thread.title, kind: thread.kind, at: thread.at }];
  });
}
